import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type {
  CascataResponse, ComponenteDto, ComponenteLoteDto, ComponenteSimularDto, CriarFichaDto, FichaEscolhaResponse, FichaResponse, ListarFichasQuery,
  CriarValorRecuperacaoDto, MarcarEscolhidaDto, NovaVersaoDto, PainelFichasQuery, PainelFichasResponse, RendimentoGrupo, SalvarSaidasDto, SimularFichaResponse, UsadaEmResponse, ValorRecuperacaoResponse,
} from '@triade/contracts';
import { ApiError, requisitar } from '@/lib/api';
import { useSessao } from '@/lib/session';
import { useConsulta, useMutacao } from './base';

export function useFichas(q: Partial<ListarFichasQuery> = {}) {
  return useConsulta<FichaResponse[]>(['fichas-tecnicas'], '/fichas-tecnicas', { query: q });
}

/** GET /fichas-tecnicas/painel (`ficha.ver`): itens PRODUTO/SERVICO com a situacao da ficha, paginado. `custo` so vem com `custo.ver`. */
export function usePainelFichas(q: Partial<PainelFichasQuery> = {}) {
  const { empresaId } = useSessao();
  return useQuery<PainelFichasResponse, ApiError>({
    queryKey: ['fichas-tecnicas', 'painel', empresaId, q],
    queryFn: () => requisitar<PainelFichasResponse>('/fichas-tecnicas/painel', { query: q }),
    enabled: empresaId !== null,
    placeholderData: keepPreviousData,
  });
}

export function useFicha(id: string | undefined) {
  return useConsulta<FichaResponse>(['fichas-tecnicas', id], `/fichas-tecnicas/${id}`, { ativo: !!id });
}

export function useCriarFicha() {
  return useMutacao<FichaResponse, CriarFichaDto>({
    metodo: 'POST', caminho: () => '/fichas-tecnicas', corpo: (v) => v, invalidar: [['fichas-tecnicas']],
  });
}

export function useAdicionarComponente() {
  return useMutacao<FichaResponse, { fichaId: string } & ComponenteDto>({
    metodo: 'POST',
    caminho: (v) => `/fichas-tecnicas/${v.fichaId}/componentes`,
    corpo: ({ fichaId: _f, ...c }) => c,
    invalidar: [['fichas-tecnicas']],
  });
}

export function useMarcarFichaEscolhida() {
  return useMutacao<FichaResponse, { fichaId: string } & MarcarEscolhidaDto>({
    metodo: 'PATCH',
    caminho: (v) => `/fichas-tecnicas/${v.fichaId}/escolhida`,
    corpo: ({ escolhida }) => ({ escolhida }),
    invalidar: [['fichas-tecnicas'], ['itens', 'resumo']],
  });
}

/**
 * Versoes da ficha do item (GET /fichas-tecnicas?itemId=), da mais nova para a mais antiga, com `emUso`.
 * `comCustoHoje` (Sessao 49, C): acrescenta `custoHoje`, `estadoCusto`, `alertasCusto` por versao. So com custo.ver (quem chama decide).
 */
export function useFichasDoItem(itemId: string | undefined, opts: { comCustoHoje?: boolean } = {}) {
  return useConsulta<FichaResponse[]>(['fichas-tecnicas', 'item'], '/fichas-tecnicas', {
    query: { itemId, limit: 200, ...(opts.comCustoHoje ? { comCustoHoje: true } : {}) },
    ativo: !!itemId,
  });
}

/**
 * GET /fichas-tecnicas/usada-em (Sessao 49, E; `ficha.ver`): fichas que usam o item (produzido pela ficha ou comprado).
 * `soEscolhidas` padrao true, como a API. A consulta refaz quando o filtro muda.
 */
export function useUsadaEm(itemId: string | undefined, soEscolhidas: boolean = true) {
  return useConsulta<UsadaEmResponse>(['fichas-tecnicas', 'usada-em'], '/fichas-tecnicas/usada-em', {
    query: { itemId, soEscolhidas: String(soEscolhidas) },
    ativo: !!itemId,
  });
}

/** GET /itens/:id/fichas/escolhas: quem trocou a ficha escolhida do item, mais nova primeiro (`ficha.ver`, sem R$). */
export function useHistoricoEscolhas(itemId: string | undefined) {
  return useConsulta<FichaEscolhaResponse[]>(['fichas-tecnicas', 'escolhas', itemId], `/itens/${itemId}/fichas/escolhas`, { ativo: !!itemId });
}

export interface SimulacaoFicha {
  /** uid das linhas, na ordem enviada: casa a resposta com as linhas da grade mesmo se elas mudarem depois. */
  uids: string[];
  resposta: SimularFichaResponse;
}

/**
 * POST /fichas-tecnicas/simular como consulta (nao grava). `entrada` nulo = desligada. Mantem o resultado anterior
 * enquanto o novo carrega. Custo de linha e total da ficha so existem aqui (ou na ficha gravada).
 */
/** Entrada da simulacao: linhas da grade e o rendimento do processo (fracao ou nulo = sem) com o grupo que o recebe. */
export interface EntradaSimulacaoFicha {
  uids: string[];
  componentes: ComponenteSimularDto[];
  rendimentoProcesso?: string | null;
  rendimentoGrupo?: RendimentoGrupo;
}

export function useSimulacaoFicha(entrada: EntradaSimulacaoFicha | null, itemId?: string) {
  const { empresaId } = useSessao();
  return useQuery<SimulacaoFicha, ApiError>({
    queryKey: [
      'ficha-simulacao',
      empresaId,
      itemId ?? null,
      entrada?.uids ?? null,
      entrada?.componentes ?? null,
      entrada?.rendimentoProcesso ?? null,
      entrada?.rendimentoGrupo ?? null,
    ],
    // `signal`: ao mudar a entrada (nova edicao) o React Query cancela a requisicao anterior ainda em voo (Sessao 37, custo em tempo real).
    queryFn: async ({ signal }) => ({
      uids: entrada?.uids ?? [],
      // itemId: a API aplica as regras do tipo do item (PRODUTO: mao de obra so no processo) e devolve `restricoes`.
      resposta: await requisitar<SimularFichaResponse>('/fichas-tecnicas/simular', {
        method: 'POST',
        sinal: signal,
        body: {
          ...(itemId ? { itemId } : {}),
          componentes: entrada?.componentes ?? [],
          rendimentoProcesso: entrada?.rendimentoProcesso ?? null,
          ...(entrada?.rendimentoGrupo ? { rendimentoGrupo: entrada.rendimentoGrupo } : {}),
        },
      }),
    }),
    enabled: entrada !== null && empresaId !== null,
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 30_000,
  });
}

/** PUT /fichas-tecnicas/:id/componentes: troca as linhas de uma ficha ainda nao usada (409 FICHA_EM_USO se ja foi). */
export function useSalvarComponentes() {
  return useMutacao<FichaResponse, { fichaId: string; descricao: string | null; componentes: ComponenteLoteDto[]; rendimentoProcesso?: string | null; rendimentoGrupo?: RendimentoGrupo }>({
    metodo: 'PUT',
    caminho: (v) => `/fichas-tecnicas/${v.fichaId}/componentes`,
    corpo: ({ descricao, componentes, rendimentoProcesso, rendimentoGrupo }) => ({
      descricao,
      componentes,
      // Ausente = a API mantem o rendimento atual; null = sem rendimento.
      ...(rendimentoProcesso !== undefined ? { rendimentoProcesso } : {}),
      ...(rendimentoGrupo ? { rendimentoGrupo } : {}),
    }),
    invalidar: [['fichas-tecnicas'], ['itens', 'resumo']],
  });
}

/** POST /fichas-tecnicas/:id/nova-versao: copia a ficha (sem `componentes`) ou grava as linhas enviadas como a proxima versao. */
export function useNovaVersaoFicha() {
  return useMutacao<FichaResponse, { fichaId: string } & NovaVersaoDto>({
    metodo: 'POST',
    caminho: (v) => `/fichas-tecnicas/${v.fichaId}/nova-versao`,
    corpo: ({ fichaId: _f, ...resto }) => resto,
    invalidar: [['fichas-tecnicas']],
  });
}

/** GET /fichas-tecnicas/:id/cascata?data=AAAA-MM-DD: arvore por nivel (processo, materiais, custo do nivel e total). `ficha.ver`. */
export function useCascata(id: string | undefined, data: string | undefined) {
  return useConsulta<CascataResponse>(['fichas-tecnicas', 'cascata', id, data ?? null], `/fichas-tecnicas/${id}/cascata`, {
    query: { data },
    ativo: !!id,
  });
}

/** PUT /fichas-tecnicas/:id/saidas: troca TODAS as saidas (subproduto e residuo) da ficha. `ficha.editar`; ficha em uso e recusada pela API. */
export function useSalvarSaidas() {
  return useMutacao<FichaResponse, { fichaId: string } & SalvarSaidasDto>({
    metodo: 'PUT',
    caminho: (v) => `/fichas-tecnicas/${v.fichaId}/saidas`,
    corpo: ({ saidas, quantidadeSaida, criterioRateio }) => ({ saidas, quantidadeSaida, criterioRateio }),
    invalidar: [['fichas-tecnicas'], ['itens', 'resumo']],
  });
}

/** GET /valores-recuperacao (`custo.ver` + `item.ver`): vigencias do valor de recuperacao; `itemId` filtra por item. Sem `itemId`, a lista toda. */
export function useValoresRecuperacao(opts: { ativo?: boolean; itemId?: string } = {}) {
  return useConsulta<ValorRecuperacaoResponse[]>(['valores-recuperacao'], '/valores-recuperacao', {
    query: opts.itemId ? { itemId: opts.itemId } : {},
    ...(opts.ativo === undefined ? {} : { ativo: opts.ativo }),
  });
}

/** Corpo de POST /valores-recuperacao: `hipotetico` fica com o padrao da API (false). */
export type NovoValorRecuperacao = Omit<CriarValorRecuperacaoDto, 'hipotetico'>;

/** POST /valores-recuperacao: nova vigencia (a anterior do item e encerrada na data). `custoAquisicao.editar`. */
export function useCriarValorRecuperacao() {
  return useMutacao<ValorRecuperacaoResponse, NovoValorRecuperacao>({
    metodo: 'POST',
    caminho: () => '/valores-recuperacao',
    corpo: (v) => v,
    invalidar: [['valores-recuperacao'], ['fichas-tecnicas']],
  });
}
